import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import supabase from '../utils/supabaseClient';
import './UsersManagement.css';

const ROLE_OPTIONS = ['admin', 'reviewer', 'writer', 'viewer'];

const UsersManagement = () => {
    const { isDarkTheme } = useTheme();

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedUser, setExpandedUser] = useState(null);
    const [userExercises, setUserExercises] = useState([]);
    const [exercisesLoading, setExercisesLoading] = useState(false);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const { data: profiles, error } = await supabase
                .from('profiles')
                .select('id, email, display_name, avatar_url, role, created_at')
                .order('created_at', { ascending: false });

            if (error) throw error;

            // Fetch contribution counts for each user
            const enriched = await Promise.all(
                (profiles || []).map(async (profile) => {
                    const { count } = await supabase
                        .from('exercises')
                        .select('*', { count: 'exact', head: true })
                        .eq('author_id', profile.id);
                    return { ...profile, contributionCount: count || 0 };
                })
            );

            setUsers(enriched);
        } catch (err) {
            console.error('[UsersManagement] Error:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleToggleExpand = async (userId) => {
        if (expandedUser === userId) {
            setExpandedUser(null);
            setUserExercises([]);
            return;
        }

        setExpandedUser(userId);
        setExercisesLoading(true);

        try {
            const { data, error } = await supabase
                .from('exercises')
                .select('id, title, status, created_at')
                .eq('author_id', userId)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setUserExercises(data || []);
        } catch (err) {
            console.error('[UsersManagement] Error fetching user exercises:', err);
            setUserExercises([]);
        } finally {
            setExercisesLoading(false);
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            const { error } = await supabase
                .from('profiles')
                .update({ role: newRole, updated_at: new Date().toISOString() })
                .eq('id', userId);

            if (error) throw error;

            setUsers(prev =>
                prev.map(u => u.id === userId ? { ...u, role: newRole } : u)
            );
        } catch (err) {
            console.error('[UsersManagement] Role update error:', err);
        }
    };

    const getRoleBadgeClass = (role) => {
        switch (role) {
            case 'admin': return 'role-admin';
            case 'reviewer': return 'role-reviewer';
            case 'writer': return 'role-writer';
            default: return 'role-viewer';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return '#22c55e';
            case 'under_review': return '#f97316';
            case 'draft': return '#a78bfa';
            default: return '#64748b';
        }
    };

    return (
        <div className={`users-mgmt ${isDarkTheme ? 'dark' : 'light'}`}>
            <div className="users-header">
                <h2>Users & Volunteers</h2>
                <span className="users-total-badge">{users.length} contributors</span>
            </div>

            {loading ? (
                <div className="admin-loading">Loading users...</div>
            ) : users.length === 0 ? (
                <div className="users-empty card">
                    <p>No users found.</p>
                </div>
            ) : (
                <div className="users-list">
                    {users.map((u) => (
                        <div key={u.id} className="users-card card">
                            <div
                                className="users-card-header"
                                onClick={() => handleToggleExpand(u.id)}
                            >
                                <div className="users-card-left">
                                    <div className="users-avatar">
                                        {u.avatar_url ? (
                                            <img src={u.avatar_url} alt={u.display_name} />
                                        ) : (
                                            <span>{(u.display_name || u.email || 'U').charAt(0).toUpperCase()}</span>
                                        )}
                                    </div>
                                    <div className="users-info">
                                        <span className="users-name">{u.display_name || u.email?.split('@')[0] || 'Unknown'}</span>
                                        <span className="users-email">{u.email || ''}</span>
                                    </div>
                                </div>
                                <div className="users-card-right">
                                    <span className="users-contribution-count">
                                        {u.contributionCount} contribution{u.contributionCount !== 1 ? 's' : ''}
                                    </span>
                                    <span className={`users-role-badge ${getRoleBadgeClass(u.role)}`}>
                                        {u.role || 'viewer'}
                                    </span>
                                    <span className={`users-expand-icon ${expandedUser === u.id ? 'expanded' : ''}`}>
                                        ⌄
                                    </span>
                                </div>
                            </div>

                            {expandedUser === u.id && (
                                <div className="users-expanded">
                                    <div className="users-role-section">
                                        <label>Role / Permission:</label>
                                        <select
                                            value={u.role || 'viewer'}
                                            onChange={(e) => handleRoleChange(u.id, e.target.value)}
                                        >
                                            {ROLE_OPTIONS.map(r => (
                                                <option key={r} value={r}>
                                                    {r.charAt(0).toUpperCase() + r.slice(1)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="users-exercises-section">
                                        <h4>Contributions</h4>
                                        {exercisesLoading ? (
                                            <p className="users-loading-text">Loading...</p>
                                        ) : userExercises.length === 0 ? (
                                            <p className="users-empty-text">No exercises yet.</p>
                                        ) : (
                                            <div className="users-exercises-list">
                                                {userExercises.map((ex) => (
                                                    <div key={ex.id} className="users-exercise-item">
                                                        <span className="users-exercise-title">{ex.title || 'Untitled'}</span>
                                                        <span
                                                            className="users-exercise-status"
                                                            style={{ color: getStatusColor(ex.status) }}
                                                        >
                                                            {ex.status?.replace(/_/g, ' ') || 'unknown'}
                                                        </span>
                                                        <span className="users-exercise-date">
                                                            {ex.created_at ? new Date(ex.created_at).toLocaleDateString() : ''}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="users-joined">
                                        Joined: {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'Unknown'}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default UsersManagement;
